import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { I18nKey } from "../../i18n/i18n";

type PasswordFieldProps = {
  label?: string;
  showRules?: boolean;
} & React.InputHTMLAttributes<HTMLInputElement>;

const PASSWORD_RULES: {
  id: string;
  label: I18nKey;
  test: (value: string) => boolean;
}[] = [
  {
    id: "length",
    // i18n.t("password-rule-length")
    label: "password-rule-length",
    test: (value: string) => value.length >= 10,
  },
  {
    id: "lowercase",
    // i18n.t("password-rule-lowercase")
    label: "password-rule-lowercase",
    test: (value: string) => /[a-z]/.test(value),
  },
  {
    id: "uppercase",
    // i18n.t("password-rule-uppercase")
    label: "password-rule-uppercase",
    test: (value: string) => /[A-Z]/.test(value),
  },
  {
    id: "number",
    // i18n.t("password-rule-number")
    label: "password-rule-number",
    test: (value: string) => /[0-9]/.test(value),
  },
];

export const PasswordField = ({
  label,
  required,
  disabled,
  showRules = false,
  value,
  ...props
}: PasswordFieldProps) => {
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const stringValue = typeof value === "string" ? value : "";

  return (
    <div>
      {label && (
        <label className="block text-tertiary mb-2">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      <div className="relative">
        <input
          {...props}
          type={showPassword ? "text" : "password"}
          value={value}
          disabled={disabled}
          className="w-full rounded-lg border border-grey-medium focus:outline-none focus:border-tertiary px-4 py-2 pr-10"
        />
        <button
          type="button"
          onClick={() => setShowPassword((prev) => !prev)}
          className="absolute inset-y-0 right-0 px-3 flex items-center text-grey-medium hover:text-tertiary"
          tabIndex={-1}
        >
          <FontAwesomeIcon icon={showPassword ? "eye-slash" : "eye"} />
        </button>
      </div>

      {showRules && (
        <ul className="mt-2 text-xs text-tertiary bg-secondary-text px-3 py-2 rounded-lg space-y-1">
          {PASSWORD_RULES.map((rule) => {
            const ok = rule.test(stringValue);
            return (
              <li key={rule.id} className="flex items-center gap-2">
                <FontAwesomeIcon
                  icon="check-circle"
                  className={ok ? "text-secondary" : "text-grey-medium"}
                />
                <span className={ok ? "font-oxaniumMedium" : ""}>
                  {t(rule.label)}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
